import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase } from "../theme";

// Public clock-in page (no login). The employee types their name and PIN, a live photo is taken and
// the phone's GPS is read at the moment they tap Clock In / Clock Out. Nothing else is tracked.
const MAX_WIDTH = 480;

function shrinkToDataUrl(source, width, height) {
  const scale = Math.min(1, MAX_WIDTH / width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
  let quality = 0.7;
  let url = canvas.toDataURL("image/jpeg", quality);
  while (url.length > 90000 && quality > 0.35) {
    quality -= 0.1;
    url = canvas.toDataURL("image/jpeg", quality);
  }
  return url;
}

function getPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

export default function ClockForm({ tenant, initialName = "", onSuccess, knownStatus = null }) {
  const [name, setName] = useState(initialName);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [hours, setHours] = useState(null);
  const [status, setStatus] = useState(knownStatus); // { clocked_in, since, has_earlier_shift_today } | null while unknown
  const [checking, setChecking] = useState(false);
  const [camera, setCamera] = useState("starting"); // starting | ready | blocked
  const [fallbackPhoto, setFallbackPhoto] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCamera("blocked");
      return undefined;
    }
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamera("ready");
      })
      .catch(() => { if (!cancelled) setCamera("blocked"); });
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (knownStatus) return undefined; // the caller (an already-identified linked employee) already knows this
    const n = name.trim();
    if (!n || pin.trim().length < 4) { setStatus(null); return undefined; }
    setChecking(true);
    const t = setTimeout(() => {
      axios.post(endpoints.clock, { tenant, name: n, pin: pin.trim(), action: "hours" })
        .then((res) => setStatus({ clocked_in: res.data.clocked_in, since: res.data.since, has_earlier_shift_today: res.data.has_earlier_shift_today }))
        .catch(() => setStatus(null))
        .finally(() => setChecking(false));
    }, 500);
    return () => { clearTimeout(t); setChecking(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, pin, tenant, knownStatus]);

  const takePhoto = () => {
    const v = videoRef.current;
    if (camera === "ready" && v && v.videoWidth) return shrinkToDataUrl(v, v.videoWidth, v.videoHeight);
    return fallbackPhoto;
  };

  const chooseFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => { setFallbackPhoto(shrinkToDataUrl(img, img.naturalWidth, img.naturalHeight)); URL.revokeObjectURL(img.src); };
    img.src = URL.createObjectURL(file);
  };

  const submit = async (action) => {
    setResult(null);
    setHours(null);
    if (!tenant) { setResult({ ok: false, text: "This link is missing its restaurant. Ask your manager for the clock-in link." }); return; }
    if (!name.trim() || !pin.trim()) { setResult({ ok: false, text: "Enter your name and PIN." }); return; }
    setBusy(action);
    try {
      const body = { tenant, name: name.trim(), pin: pin.trim(), action };
      if (action !== "hours") {
        const photo = takePhoto();
        if (!photo) {
          setResult({ ok: false, text: "A photo is needed. Allow camera access (or choose a photo below), then try again, or ask a manager to punch you in." });
          setBusy("");
          return;
        }
        body.photo = photo;
        Object.assign(body, await getPosition());
      }
      const res = await axios.post(endpoints.clock, body);
      if (action === "hours") {
        setHours(res.data);
      } else {
        setResult({ ok: true, text: res.data.message, warning: res.data.warning });
        setStatus({ clocked_in: res.data.clocked_in, since: res.data.since, has_earlier_shift_today: res.data.has_earlier_shift_today });
        setName(initialName);
        if (onSuccess) onSuccess(res.data);
        setFallbackPhoto(null);
      }
      setPin("");
    } catch (err) {
      setResult({ ok: false, text: err.response?.data?.error || "Something went wrong. Please try again." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div style={{ padding: space[2] }} data-testid="clock-form">
          
          <input style={styles.input} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          <input
            style={{ ...styles.input, marginTop: space[2], letterSpacing: "0.2em" }}
            placeholder="PIN"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            autoComplete="off"
          />

          <div style={styles.cameraBox}>
            {camera !== "blocked" ? (
              <video ref={videoRef} autoPlay playsInline muted style={styles.video} aria-label="Camera preview" />
            ) : fallbackPhoto ? (
              <img src={fallbackPhoto} alt="Your snapshot" style={styles.video} />
            ) : (
              <div style={styles.cameraOff}>Camera unavailable</div>
            )}
          </div>
          {camera === "starting" && <p style={styles.hint}>Starting the camera…</p>}
          {camera === "blocked" && (
            <div style={styles.hint}>
              <p style={{ margin: "0 0 6px" }}>We couldn't use the camera. Allow camera access for this site in your browser settings, or take a photo instead:</p>
              <input type="file" accept="image/*" capture="user" onChange={chooseFile} aria-label="Take a photo" />
            </div>
          )}

          {status ? (
            <div style={{ display: "flex", gap: space[2], marginTop: space[3] }} data-testid="clock-action">
              {status.clocked_in ? (
                <button style={{ ...buttonSecondary, flex: 1 }} onClick={() => submit("out")} disabled={!!busy}>{busy === "out" ? "Clocking out…" : `Clock Out · since ${status.since ? (status.since.length > 5 ? status.since.slice(11) : status.since) : ""}`}</button>
              ) : (
                <button style={{ ...buttonPrimary, flex: 1 }} onClick={() => submit("in")} disabled={!!busy}>
                  {busy === "in" ? "Clocking in…" : status.has_earlier_shift_today ? "+ Clock In (new shift today)" : "Clock In"}
                </button>
              )}
            </div>
          ) : (
            // Before we've confirmed who's typing (empty fields, or a name/PIN that hasn't matched anyone yet),
            // both actions stay available exactly as before, so a mistake still gets the normal server message
            // (e.g. "Name or PIN incorrect") instead of a button that's stuck disabled.
            <div style={{ display: "flex", gap: space[2], marginTop: space[3] }} data-testid="clock-action">
              <button style={{ ...buttonPrimary, flex: 1 }} onClick={() => submit("in")} disabled={!!busy}>{busy === "in" ? "Clocking in…" : "Clock In"}</button>
              <button style={{ ...buttonSecondary, flex: 1 }} onClick={() => submit("out")} disabled={!!busy}>{busy === "out" ? "Clocking out…" : "Clock Out"}</button>
            </div>
          )}
          <button style={{ ...styles.linkBtn }} onClick={() => submit("hours")} disabled={!!busy}>{busy === "hours" ? "Loading…" : "View my hours (last 10 days)"}</button>

          {result && (
            <div role="status" style={{ ...styles.msg, ...(result.ok ? styles.ok : styles.err) }} data-testid="clock-result">
              {result.text}
              {result.warning && <div style={{ marginTop: 6, fontSize: 12 }}>{result.warning}</div>}
            </div>
          )}

          {hours && (
            <div style={styles.hours} data-testid="clock-hours">
              <div style={{ fontWeight: 600 }}>{hours.name}: {hours.clocked_in ? `clocked in since ${hours.since.slice(11)}` : "clocked out"}</div>
              {hours.days.length === 0 && <p style={styles.hint}>No hours recorded in the last 10 days.</p>}
              {hours.days.map((d) => (
                <div key={d.date} style={styles.dayRow}>
                  <span>{d.date}</span>
                  <span>{d.entries.map((e) => `${e.in} to ${e.out || "now"}`).join(", ")}</span>
                  <strong>{d.hhmm}</strong>
                </div>
              ))}
              {hours.days.length > 0 && <div style={{ ...styles.dayRow, borderBottom: "none" }}><span>Total</span><span /><strong>{hours.total_hhmm}</strong></div>}
            </div>
          )}

          <p style={styles.consent}>
            Your location and a photo are recorded when you clock in and out. Only your manager can see them.
          </p>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: colors.bgPage, padding: space[4], boxSizing: "border-box" },
  card: { width: "100%", maxWidth: 420, background: colors.surface, borderRadius: radius.lg, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" },
  heading: { margin: `0 0 ${space[3]}`, fontFamily: fonts.display, fontSize: 19, fontWeight: 600, textAlign: "center", color: colors.textPrimary },
  input: { ...inputBase, width: "100%", boxSizing: "border-box" },
  cameraBox: { marginTop: space[3], background: "#111", borderRadius: radius.md, overflow: "hidden", aspectRatio: "4 / 3", display: "flex", alignItems: "center", justifyContent: "center" },
  video: { width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" },
  cameraOff: { color: "#9ca3af", fontFamily: fonts.body, fontSize: 13 },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: space[2] },
  linkBtn: { display: "block", width: "100%", marginTop: space[2], background: "none", border: "none", color: colors.brandBlue, fontFamily: fonts.body, fontSize: 14, cursor: "pointer", padding: space[2] },
  msg: { marginTop: space[3], padding: space[3], borderRadius: radius.sm, fontFamily: fonts.body, fontSize: 14 },
  ok: { background: "#ecfdf3", color: "#067647" },
  err: { background: "#fef3f2", color: "#b42318" },
  hours: { marginTop: space[3], fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
  dayRow: { display: "flex", justifyContent: "space-between", gap: space[2], padding: "6px 0", borderBottom: `1px solid ${colors.border}` },
  consent: { fontFamily: fonts.body, fontSize: 11, color: colors.textMuted, textAlign: "center", marginTop: space[4], marginBottom: 0 },
};
